import React from 'react';
import { X, RefreshCw, Download } from 'lucide-react';
import { Template, FillTemplateKey } from './types';
import DocumentPreview from './DocumentPreview';

interface FillModalProps {
    selectedTemplate: Template;
    onClose: () => void;
    templateKeys: FillTemplateKey[];
    handleKeyValueChange: (index: number, value: string) => void;
    generateDocument: () => Promise<void>;
    generatedDocument: string | null;
    downloadDocument: () => Promise<void>;
}

const FillModal: React.FC<FillModalProps> = ({
    selectedTemplate,
    onClose,
    templateKeys,
    handleKeyValueChange,
    generateDocument,
    generatedDocument,
    downloadDocument,
}) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">Şablon Doldur</h2>
                            <p className="text-gray-600">{selectedTemplate.name}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="p-6">
                    {!generatedDocument || generatedDocument === 'processing' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    Alanları Doldurun
                                </h3>
                                <div className="space-y-4">
                                    {templateKeys.map((key, index) => (
                                        <div key={index}>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {key.key_name.replace(/_/g, ' ').toUpperCase()}
                                                {key.is_required && (
                                                    <span className="text-red-500 ml-1">*</span>
                                                )}
                                            </label>
                                            <input
                                                type={
                                                    key.data_type === 'date'
                                                        ? 'date'
                                                        : key.data_type === 'number'
                                                            ? 'number'
                                                            : key.data_type
                                                }
                                                value={key.value}
                                                onChange={(e) => handleKeyValueChange(index, e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder={`${key.key_name} değerini girin...`}
                                                required={key.is_required}
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8">
                                    <button
                                        onClick={generateDocument}
                                        disabled={generatedDocument === 'processing'}
                                        className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center transition-colors ${generatedDocument === 'processing'
                                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                                : 'bg-blue-900 text-white hover:bg-blue-800'
                                            }`}
                                    >
                                        {generatedDocument === 'processing' ? (
                                            <>
                                                <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                                                Döküman Oluşturuluyor...
                                            </>
                                        ) : (
                                            <>
                                                <Download className="h-5 w-5 mr-2" />
                                                Dökümanı Oluştur ve İndir
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Önizleme</h3>
                                <DocumentPreview
                                    templateKeys={templateKeys}
                                    originalFilename={selectedTemplate.original_filename}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <div className="bg-green-100 text-green-600 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <Download className="h-8 w-8" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                Döküman Başarıyla Oluşturuldu!
                            </h3>
                            <p className="text-gray-600 mb-8">
                                <strong>{generatedDocument}</strong> isimli döküman hazır.
                            </p>

                            <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                                <button
                                    onClick={downloadDocument}
                                    className="bg-blue-900 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-800 transition-colors flex items-center justify-center"
                                >
                                    <Download className="h-5 w-5 mr-2" />
                                    Hemen İndir
                                </button>
                                <button
                                    onClick={onClose}
                                    className="bg-gray-100 text-gray-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                                >
                                    Kapat
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FillModal;
